import { D2CFunnelStep, D2CSourceApp } from '@orcar/common';
import { createFunnelTracker, type FunnelTracker } from '@orcar/common-d2c';
import mixpanel from 'mixpanel-browser';
import { SUPER_INSURANCE_EXPERIMENT } from '../constants/experiment';

// 이벤트 이름 리터럴 타입
export type AnalyticsEventName =
  | 'page_view'
  | 'homepage_viewed'
  | 'search_started'
  | 'car_detailed_viewed'
  | 'booking_started'
  | 'reservation_created'
  | 'payment_successful'
  | 'payment_failed'
  | 'checkout_started'
  | 'guest_checkout_clicked'
  | 'signup_discount_clicked'
  | 'signup_started'
  | 'signup'
  | 'sms_clicked'
  | 'banner_clicked'
  | 'banner_viewed'
  | 'button_clicked'
  // 반려동물 동반("펫과 함께")
  | 'pet_filter_toggled'
  | 'pet_policy_guide_viewed'
  | 'pet_policy_agreed'
  | 'pet_reservation_viewed'
  // 자차보험 선택(완전면책 게이지/애니메이션 UX 성과 측정)
  | 'insurance_section_viewed'
  | 'insurance_option_selected'
  | 'insurance_option_deselected'
  // 친구추천(레퍼럴) 퍼널: 초대링크 진입 → 코드 적용 → 결제 할인 적용
  | 'referral_link_opened'
  | 'referral_code_submitted'
  | 'referral_discount_applied';

// 공통 속성 타입 정의
export type Platform = 'web';
export type DeviceType = 'mobile' | 'desktop';
export type UserType = 'guest' | 'member';
/** 차량 선택/예약이 시작된 진입 경로(홈 인기차량 vs 검색결과) */
export type EntryPoint = 'home_popular' | 'search_result';
export type Channel =
  | 'direct'
  | 'organic'
  | 'social'
  | 'referral'
  | 'facebook_ads'
  | 'google_ads'
  | 'kakao'
  | 'sms';

// 공통 이벤트 속성 인터페이스
export interface CommonEventProperties {
  app_name: string;
  platform: Platform;
  device: DeviceType;
  user_type: UserType;
  channel: Channel;
  environment: 'prod' | 'dev';
}

// 기본 이벤트 속성 인터페이스
export interface BaseEventProperties {
  timestamp?: number;
  [key: string]: unknown;
}

// 반려동물 동반("펫과 함께") 공통 속성
export interface PetEventProperties extends BaseEventProperties {
  is_with_pet?: boolean;
  pet_type?: string | null;
  pet_count?: number | null;
  pet_total_weight_kg?: number | null;
  pet_price_rate?: number | null;
}

// 페이지 뷰 이벤트 속성
export interface PageViewEventProperties extends BaseEventProperties {
  page_name: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  campaign_channel?: string;
}

// 검색 이벤트 속성
export interface SearchEventProperties extends BaseEventProperties {
  pickUpDate?: string;
  dropOffDate?: string;
  location?: string;
  is_with_pet?: boolean;
}

// 차량 상세 이벤트 속성
export interface CarDetailEventProperties extends BaseEventProperties {
  car_id: string;
  car_model: string;
  car_type: string;
  is_with_pet?: boolean;
  pet_price_rate?: number | null;
  /** 진입 경로: home_popular(홈 인기차량) | search_result(검색결과) */
  source?: EntryPoint;
}

// 히어로 배너 이벤트 속성
export interface BannerEventProperties extends BaseEventProperties {
  banner_id?: string;
  banner_index?: number;
  page?: string;
}

// 버튼 클릭 이벤트 속성
export interface ButtonClickEventProperties extends BaseEventProperties {
  button_name: string;
  page: string;
}

// 예약 시작 이벤트 속성
export interface BookingStartedEventProperties extends BaseEventProperties {
  car_id: string;
  car_type: string;
  dates: string;
  is_with_pet?: boolean;
  /** 진입 경로: home_popular(홈 인기차량) | search_result(검색결과) */
  source?: EntryPoint;
}

// 예약 생성 이벤트 속성
export interface ReservationEventProperties extends BaseEventProperties {
  reservation_id: string;
  car_id: string;
  car_type: string;
  total_amount: number;
  is_with_pet?: boolean;
  pet_type?: string | null;
  pet_count?: number | null;
  // ── 완전면책 가격 실험 측정용(완료 시점 스냅샷) ──
  /** 차량 기본 자차 타입: NORMAL | PERFECT | SUPER */
  base_insurance?: string;
  /** 완료 시 완전면책 가입 결과: 'super' | 'base' */
  insurance_selected?: 'super' | 'base';
  /** 대여일수(캘린더 일수차 + 1, 서버 기준 동일) */
  rental_days?: number;
  /** 완전면책 총 특약금액(관측값 = 화면에 노출된 가격) */
  insurance_price_total?: number;
  /** 완전면책 1일 단가(관측값 = 총액/일수) */
  insurance_price_per_day?: number;
  /** 로그인 회원 여부(회원가/게스트가 구분) */
  is_member?: boolean;
}

// 홈페이지 방문 이벤트 속성
export interface HomepageViewedEventProperties extends BaseEventProperties {
  source?: string;
  referrer?: string;
}

// 체크아웃 시작 이벤트 속성
export interface CheckoutStartedEventProperties extends BaseEventProperties {
  guest_checkout: boolean;
  reservation_id?: string;
}

// 회원가입 이벤트 속성
export interface SignupEventProperties extends BaseEventProperties {
  from_discount_cta: boolean;
  user_id?: string;
}

// 자차보험 선택 이벤트 속성
export interface InsuranceEventProperties extends BaseEventProperties {
  car_id?: string;
  car_type?: string;
  /** 차량 기본 자차 타입: NORMAL | PERFECT | SUPER */
  base_insurance?: string;
  /** 선택 결과: 'super'(완전면책 가입) | 'base'(기본) */
  selected?: 'super' | 'base';
  /** 완전면책 가입 가능 여부 */
  can_add_super?: boolean;
  /** 완전면책 특약 금액 */
  price_delta?: number;
  /** 선택 시점의 총 결제 금액 */
  total_price?: number;
  /** 선택이 발생한 단계(중복 집계 방지용 구분자) */
  step?: 'order_confirmation' | 'payment';
  /** 대여일수(캘린더 일수차 + 1, 서버 기준 동일) */
  rental_days?: number;
  /** 완전면책 총 특약금액(관측값 = 화면에 노출된 가격) */
  insurance_price_total?: number;
  /** 완전면책 1일 단가(관측값 = 총액/일수) */
  insurance_price_per_day?: number;
  /** 로그인 회원 여부(회원가/게스트가 구분) */
  is_member?: boolean;
}

// 친구추천(레퍼럴) 이벤트 속성
export interface ReferralEventProperties extends BaseEventProperties {
  /** 사용된 추천 코드 */
  referral_code?: string;
  /** 코드 유입 경로: 초대링크 자동 / 결제창 직접입력 */
  referral_source?: 'invite_link' | 'checkout_input';
  /** 결제에 적용된 추천 할인액(원) */
  discount_amount?: number;
}

// 모든 이벤트 속성 타입의 유니온
export type EventProperties =
  | InsuranceEventProperties
  | PageViewEventProperties
  | SearchEventProperties
  | CarDetailEventProperties
  | ButtonClickEventProperties
  | ReservationEventProperties
  | HomepageViewedEventProperties
  | BookingStartedEventProperties
  | CheckoutStartedEventProperties
  | SignupEventProperties
  | ReferralEventProperties;

// Mixpanel 초기화 상태
let isInitialized = false;

// 디바이스 타입 감지
function detectDevice(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';

  const userAgent = window.navigator.userAgent.toLowerCase();
  const mobileKeywords = ['mobile', 'android', 'iphone'];

  return mobileKeywords.some((keyword) => userAgent.includes(keyword))
    ? 'mobile'
    : 'desktop';
}

// 사용자 타입 감지
function detectUserType(): UserType {
  if (typeof window === 'undefined') return 'guest';

  // localStorage에서 인증 상태 확인
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return 'guest';

  try {
    const authData = JSON.parse(authStorage);
    return authData?.state?.isLoggedIn ? 'member' : 'guest';
  } catch {
    return 'guest';
  }
}

// 채널 감지 (UTM 기반)
function detectChannel(): Channel {
  if (typeof window === 'undefined') return 'direct';

  // localStorage에서 UTM 정보 확인
  const utmSource = localStorage.getItem('utm_source');
  const utmMedium = localStorage.getItem('utm_medium');

  if (!utmSource) return 'direct';

  // UTM 기반 채널 매핑
  if (utmSource.includes('facebook')) return 'facebook_ads';
  if (utmSource.includes('google')) return 'google_ads';
  if (utmSource.includes('kakao')) return 'kakao';
  if (utmSource.includes('sms')) return 'sms';

  // Medium 기반 분류
  if (utmMedium && utmMedium === 'social') return 'social';
  if (utmMedium && utmMedium === 'organic') return 'organic';
  if (utmMedium && utmMedium === 'referral') return 'referral';

  return 'direct';
}

// 공통 이벤트 속성 생성
function getCommonProperties(): CommonEventProperties {
  // 파트너별 Production 도메인 체크
  const productionDomains = {
    jejuro: ['jejurorentcar.com', 'www.jejurorentcar.com'],
  };

  const appName = import.meta.env.VITE_PACKAGE_NAME || 'jejuro';
  const currentDomains =
    productionDomains[appName as keyof typeof productionDomains] || [];
  const isProduction = currentDomains.includes(window.location.hostname);

  return {
    app_name: appName,
    platform: 'web' as Platform,
    device: detectDevice(),
    user_type: detectUserType(),
    channel: detectChannel(),
    environment: isProduction ? 'prod' : 'dev',
  };
}

// 안전하게 Mixpanel 호출하는 유틸리티
const safeMixpanelCall = (callback: () => void): void => {
  try {
    callback();
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

// UTM 파라미터 처리 함수
export function captureUtmParameters(): void {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term'),
    campaign_channel: urlParams.get('campaign_channel'),
  };

  // UTM 파라미터가 있는 경우에만 localStorage에 저장
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
    }
  });

  captureLandingContext();

  // Mixpanel에도 UTM 정보 등록 (슈퍼 프로퍼티로)
  if (isInitialized) {
    const validUtmParams = Object.fromEntries(
      Object.entries(utmParams).filter(([_, value]) => value !== null),
    );

    if (Object.keys(validUtmParams).length > 0) {
      registerProperties(validUtmParams);
    }
  }
}

// ── 마케팅 어트리뷰션 (first-touch / last-touch) ──────────
const ATTRIBUTION_FIRST_KEY = 'attribution_first';
const ATTRIBUTION_LAST_KEY = 'attribution_last';

/** 한 번의 유입 접점(터치) 스냅샷. 서버로 전송해 d2c_attribution에 저장한다. */
export interface AttributionTouch {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  /** Google Click ID */
  gclid: string | null;
  /** Meta(Facebook) Click ID */
  fbclid: string | null;
  referrerDomain: string | null;
  landingPath: string;
  capturedAt: string;
}

function getReferrerDomain(): string | null {
  try {
    if (!document.referrer) return null;
    return new URL(document.referrer).hostname;
  } catch {
    return null;
  }
}

/**
 * 현재 방문의 유입 정보를 캡처한다.
 * - first-touch: 최초 유입 1회만 저장(영구)
 * - last-touch: 유의미한 유입 신호가 있을 때마다 갱신
 * 내부 이동/직접 방문(신호 없음)은 노이즈라 기록하지 않는다.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const referrerDomain = getReferrerDomain();
  const isSelfReferrer = !!referrerDomain?.includes('jejurorentcar.com');

  const touch: AttributionTouch = {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
    utmTerm: params.get('utm_term'),
    gclid: params.get('gclid'),
    fbclid: params.get('fbclid'),
    referrerDomain,
    landingPath: window.location.pathname,
    capturedAt: new Date().toISOString(),
  };

  const hasSignal =
    !!touch.utmSource ||
    !!touch.gclid ||
    !!touch.fbclid ||
    (!!referrerDomain && !isSelfReferrer);
  if (!hasSignal) return;

  const serialized = JSON.stringify(touch);
  if (!localStorage.getItem(ATTRIBUTION_FIRST_KEY)) {
    localStorage.setItem(ATTRIBUTION_FIRST_KEY, serialized);
  }
  localStorage.setItem(ATTRIBUTION_LAST_KEY, serialized);
}

/** 저장된 first/last-touch 어트리뷰션을 반환(서버 전송용). */
export function getStoredAttribution(): {
  first: AttributionTouch | null;
  last: AttributionTouch | null;
} {
  const parse = (key: string): AttributionTouch | null => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as AttributionTouch) : null;
    } catch {
      return null;
    }
  };
  return {
    first: parse(ATTRIBUTION_FIRST_KEY),
    last: parse(ATTRIBUTION_LAST_KEY),
  };
}

// 초기화 함수
// ── ERP 애널리틱스용 유입 어트리뷰션 ──
// Mixpanel에만 남던 채널 정보를 예약 확정 요청에 실어 ERP DB에도 남긴다.
// 이게 있어야 "어느 광고가 매출을 냈나"를 실제 예약·매출과 조인할 수 있다.

const LANDING_PATH_KEY = 'attribution_landing_path';
const REFERRER_HOST_KEY = 'attribution_referrer_host';

/** 첫 진입 경로와 referrer 호스트를 최초 1회만 저장한다(first-touch). */
function captureLandingContext(): void {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(LANDING_PATH_KEY)) return;

  localStorage.setItem(LANDING_PATH_KEY, window.location.pathname);

  if (document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      // 사이트 내부 이동은 유입 출처가 아니다.
      if (referrerHost !== window.location.hostname) {
        localStorage.setItem(REFERRER_HOST_KEY, referrerHost);
      }
    } catch {
      // 파싱 불가한 referrer는 그냥 버린다.
    }
  }
}

/** 예약 확정 요청(`/pre-reservations/:id/confirm`)에 실을 어트리뷰션 필드. */
export function getReservationAttribution(): {
  sourceApp: D2CSourceApp;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPath?: string;
  referrerHost?: string;
} {
  const read = (key: string) =>
    (typeof window === 'undefined' ? null : localStorage.getItem(key)) ||
    undefined;

  return {
    sourceApp: D2CSourceApp.JEJURO,
    utmSource: read('utm_source'),
    utmMedium: read('utm_medium'),
    utmCampaign: read('utm_campaign'),
    utmContent: read('utm_content'),
    utmTerm: read('utm_term'),
    landingPath: read(LANDING_PATH_KEY),
    referrerHost: read(REFERRER_HOST_KEY),
  };
}

export function initAnalytics(): void {
  // 실제 도메인 기반으로 환경 판단
  const productionDomains = ['jejurorentcar.com', 'www.jejurorentcar.com'];
  const isProduction = productionDomains.includes(window.location.hostname);

  // 통합된 Mixpanel 토큰 사용
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;

  console.log('🔍 Mixpanel 초기화 시작:', {
    isProduction,
    hostname: window.location.hostname,
    token: token ? '설정됨' : '없음',
    MODE: import.meta.env.MODE,
  });

  if (!token) {
    console.warn(`Analytics token is empty. Tracking disabled.`);
    return;
  }

  safeMixpanelCall(() => {
    // Mixpanel 초기화
    mixpanel.init(token, {
      debug: import.meta.env.MODE === 'development',
      track_pageview: false,
      record_sessions_percent: 0, // 세션 리플레이는 Clarity로 본다
      autocapture: {
        pageview: 'full-url', // 전체 URL로 페이지뷰 캡처
        click: true, // 클릭 추적 활성화
        input: true, // 입력 필드 추적 활성화
        scroll: true, // 스크롤 추적 활성화
        submit: true, // 폼 제출 추적 활성화
        capture_text_content: false, // 텍스트 내용 캡처 비활성화 (개인정보 보호)
      },
    });

    mixpanel.register({
      environment: isProduction ? 'prod' : 'dev',
    });

    isInitialized = true;
    console.log('✅ Mixpanel 초기화 완료 (Autocapture 활성화)');
  });
}

/**
 * Mixpanel 이벤트를 자사 예약 퍼널 단계로 매핑한다.
 * 여기 있는 이벤트만 `d2c_funnel_event`로도 전송된다(홈→검색→차량→예약→결제→확정).
 */
const EVENT_TO_FUNNEL_STEP: Partial<Record<AnalyticsEventName, D2CFunnelStep>> =
  {
    // 홈은 page_view가 아니라 homepage_viewed를 찍으므로 둘 다 방문(진입)으로 본다.
    page_view: D2CFunnelStep.SESSION_ENTRY,
    homepage_viewed: D2CFunnelStep.SESSION_ENTRY,
    search_started: D2CFunnelStep.SEARCH_STARTED,
    car_detailed_viewed: D2CFunnelStep.VEHICLE_VIEWED,
    booking_started: D2CFunnelStep.BOOKING_STARTED,
    // 결제 시작(PAYMENT_STARTED)은 Mixpanel 이벤트가 없어 trackPaymentStarted로 따로 보낸다.
    reservation_created: D2CFunnelStep.RESERVATION_CREATED,
  };

let funnelTracker: FunnelTracker | null = null;
function sendFunnelStep(step: D2CFunnelStep): void {
  funnelTracker ??= createFunnelTracker({
    endpoint: `${import.meta.env.VITE_SERVER_URL}/funnel-events`,
    companyId: Number(import.meta.env.VITE_COMPANY_ID),
    sourceApp: D2CSourceApp.JEJURO,
    getAttribution: getReservationAttribution,
  });
  funnelTracker.trackStep(step);
}

function trackFunnelStep(eventName: AnalyticsEventName): void {
  const step = EVENT_TO_FUNNEL_STEP[eventName];
  if (step) sendFunnelStep(step);
}

/**
 * 결제 화면에서 손님이 고객 정보를 제출하는 결제(현장 결제는 예약) 버튼을 누른 순간을
 * 자사 퍼널에만 남긴다(Mixpanel 이벤트 없음). 확인 창·저장 요청보다 먼저 부른다.
 */
export function trackPaymentStarted(): void {
  sendFunnelStep(D2CFunnelStep.PAYMENT_STARTED);
}

// 이벤트 추적 (타입 안전) - 공통 속성 자동 추가
export function trackEvent(
  eventName: AnalyticsEventName,
  properties: Partial<EventProperties>,
): void {
  // 자사 퍼널 전송은 Mixpanel 초기화와 무관하게 항상 먼저 수행한다.
  // (갤럭시처럼 Mixpanel 토큰이 없어 init이 안 돼도 퍼널은 잡혀야 하므로)
  trackFunnelStep(eventName);

  if (!isInitialized) {
    console.warn('❌ Mixpanel이 초기화되지 않았습니다:', eventName);
    return;
  }

  const commonProps = getCommonProperties();

  console.log('📊 이벤트 트래킹:', {
    event: eventName,
    page: window.location.pathname,
    environment: commonProps.environment,
  });

  safeMixpanelCall(() => {
    mixpanel.track(eventName, {
      ...commonProps,
      ...properties,
      timestamp: Date.now(),
    });
  });
}

// 슈퍼 프로퍼티 등록
export function registerProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) return;

  safeMixpanelCall(() => {
    mixpanel.register(properties);
  });
}

// 완전면책 가격 A/B 실험 식별자를 슈퍼 프로퍼티로 등록
// (UTM과 동일하게 이후 모든 이벤트에 자동 첨부 → price_version 별 코호트 분석 가능)
export function registerExperimentProperties(): void {
  registerProperties({
    experiment_id: SUPER_INSURANCE_EXPERIMENT.id,
    price_version: SUPER_INSURANCE_EXPERIMENT.version,
    price_applied_at: SUPER_INSURANCE_EXPERIMENT.appliedAt,
  });
}

// 세션 리셋
export function reset(): void {
  if (!isInitialized) return;

  safeMixpanelCall(() => {
    mixpanel.reset();
  });
}

// 사용자 식별 함수
export function identifyUser(
  userId: string,
  userProperties?: Record<string, unknown>,
): void {
  if (!isInitialized) {
    console.warn('❌ Mixpanel이 초기화되지 않았습니다: identifyUser');
    return;
  }

  safeMixpanelCall(() => {
    mixpanel.identify(userId);

    if (userProperties) {
      mixpanel.people.set(userProperties);
    }

    console.log('👤 사용자 식별 완료:', {
      userId,
      userProperties,
    });
  });
}

// 사용자 프로퍼티 설정
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) {
    console.warn('❌ Mixpanel이 초기화되지 않았습니다: setUserProperties');
    return;
  }

  safeMixpanelCall(() => {
    mixpanel.people.set(properties);
    console.log('👤 사용자 프로퍼티 설정:', properties);
  });
}

// 요구사항에 따른 이벤트 추적 함수들
export function trackHomepageViewed(
  properties: Partial<HomepageViewedEventProperties> = {},
): void {
  trackEvent('homepage_viewed', properties);
}

export function trackSearchStarted(
  properties: Partial<SearchEventProperties> = {},
): void {
  trackEvent('search_started', properties);
}

export function trackCarDetailedViewed(
  properties: Partial<CarDetailEventProperties> = {},
): void {
  trackEvent('car_detailed_viewed', properties);
}

export function trackBookingStarted(
  properties: Partial<BookingStartedEventProperties> = {},
): void {
  trackEvent('booking_started', properties);
}

export function trackReservationCreated(
  properties: Partial<ReservationEventProperties> = {},
): void {
  trackEvent('reservation_created', properties);
}

export function trackPaymentSuccessful(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('payment_successful', properties);
}

export function trackPaymentFailed(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('payment_failed', properties);
}

// ── 친구추천(레퍼럴) 퍼널 ─────────────────────────────
export function trackReferralLinkOpened(
  properties: Partial<ReferralEventProperties> = {},
): void {
  trackEvent('referral_link_opened', properties);
}

export function trackReferralCodeSubmitted(
  properties: Partial<ReferralEventProperties> = {},
): void {
  trackEvent('referral_code_submitted', properties);
}

export function trackReferralDiscountApplied(
  properties: Partial<ReferralEventProperties> = {},
): void {
  trackEvent('referral_discount_applied', properties);
}

export function trackCheckoutStarted(
  properties: Partial<CheckoutStartedEventProperties> = {},
): void {
  trackEvent('checkout_started', {
    guest_checkout: true,
    ...properties,
  });
}

export function trackGuestCheckoutClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('guest_checkout_clicked', properties);
}

export function trackSignupDiscountClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('signup_discount_clicked', properties);
}

export function trackSignupStarted(
  provider: 'naver' | 'kakao',
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('signup_started', {
    ...properties,
    provider,
  });
}

export function trackSignup(
  properties: Partial<SignupEventProperties> = {},
): void {
  trackEvent('signup', {
    from_discount_cta: true,
    ...properties,
  });
}

export function trackSmsClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('sms_clicked', properties);
}

export function trackBannerClicked(
  properties: Partial<BannerEventProperties> = {},
): void {
  trackEvent('banner_clicked', properties);
}

export function trackBannerViewed(
  properties: Partial<BannerEventProperties> = {},
): void {
  trackEvent('banner_viewed', properties);
}

export function trackButtonClick(
  buttonName: string,
  properties: Partial<ButtonClickEventProperties> = {},
): void {
  trackEvent('button_clicked', {
    button_name: buttonName,
    ...properties,
  });
}

// 페이지 뷰 추적
export function trackPageView(
  pageName: string,
  properties: Partial<PageViewEventProperties> = {},
): void {
  trackEvent('page_view', {
    page_name: pageName,
    ...properties,
  });
}

// 반려동물 동반("펫과 함께") 전용 이벤트
export function trackPetFilterToggled(
  enabled: boolean,
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('pet_filter_toggled', { ...properties, enabled });
}

export function trackPetPolicyGuideViewed(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('pet_policy_guide_viewed', properties);
}

export function trackPetPolicyAgreed(
  method: 'guide_confirm' | 'manual',
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('pet_policy_agreed', { ...properties, method });
}

export function trackPetReservationViewed(
  properties: Partial<PetEventProperties> = {},
): void {
  trackEvent('pet_reservation_viewed', properties);
}

// ── 자차보험 선택 UX 성과 측정 ──
export function trackInsuranceSectionViewed(
  properties: Partial<InsuranceEventProperties> = {},
): void {
  trackEvent('insurance_section_viewed', properties);
}

export function trackInsuranceOptionSelected(
  properties: Partial<InsuranceEventProperties> = {},
): void {
  trackEvent('insurance_option_selected', { selected: 'super', ...properties });
}

export function trackInsuranceOptionDeselected(
  properties: Partial<InsuranceEventProperties> = {},
): void {
  trackEvent('insurance_option_deselected', {
    selected: 'base',
    ...properties,
  });
}
